import { Injectable, EventEmitter, Injector } from '@angular/core';
import { StorageService } from 'src/app/services/storage.service';
import { AlertService } from 'src/app/services/alert.service';
import { ArboService } from 'src/app/services/arbo.service';
import { ClientService } from 'src/app/services/client.service';
import { HttpService } from 'src/app/services/http.service';
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { UserService } from './user.service';
import { SectionService } from './section.service';
import { DashboardService } from './dashboard.service';
import { AccountService } from './account.service';
import { CampaignService } from './campaign.service';
import { SourceService } from './source.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService implements CanActivate {
  public menuIndex = 0;
  public user;
  public sectionId = 1;
  public membership;
  public account;
  public accounts;
  public loaded;
  public accountId; // Será mantido sincronizado com account.id
  public currentSourceId;

  // Adiciona um EventEmitter para notificar mudanças de conta
  public accountChanged = new EventEmitter<void>();

  // EventEmitter para notificar mudanças de organização (membership/sectionId)
  public membershipChanged = new EventEmitter<void>();

  private _resolve;

  private _sectionService: SectionService | null = null;
  private get sectionService(): SectionService {
    if (!this._sectionService) {
      this._sectionService = this.injector.get(SectionService);
    }
    return this._sectionService;
  }

  private _dashboardService: DashboardService | null = null;
  private get dashboardService(): DashboardService {
    if (!this._dashboardService) {
      this._dashboardService = this.injector.get(DashboardService);
    }
    return this._dashboardService;
  }


  constructor(
    private router: Router,
    private alertService: AlertService,
    private arboService: ArboService,
    private userService: UserService,
    private clientService: ClientService,
    private storageService: StorageService,
    private httpService: HttpService,
    private injector: Injector
  ) {
    this.loaded = new Promise((resolve) => {
      this._resolve = resolve;
    });
    this.loadPreviousSession();
  }

  async loadPreviousSession() {
    try {
      this.user = this.storageService.get('user');
    } catch (e) {
      localStorage.removeItem('user');
      this.user = null;
    }

    if (this.user && this.user.token) {
      // Verifica se o token está expirado
      if (this.isTokenExpired(this.user.token)) {
        this.httpService.setKey(null);
        this.user = null;
        localStorage.removeItem('user');
        localStorage.removeItem('selectedAccountId');
        this._resolve(null);
        return;
      }
      try {
        await this.setupSession(this.user.token);
      } catch (e) {
        // setupSession falhou (rede, circular dep, etc.)
        // MANTEM o usuario logado com dados locais — NÃO limpa localStorage
        console.warn('Erro ao restaurar sessão completa (offline?):', e);
        // Configura sessão mínima com dados do localStorage
        this.httpService.setKey(this.user.token.token);
        this.accounts = this.user.accounts || [];
        const selectedAccountId = localStorage.getItem('selectedAccountId');
        if (selectedAccountId && this.accounts.length > 0) {
          this.account = this.accounts.find(acc => acc.id === parseInt(selectedAccountId, 10)) || this.accounts[0];
          this.accountId = this.account?.id;
        } else if (this.accounts.length > 0) {
          this.account = this.accounts[0];
          this.accountId = this.account.id;
        }
        try {
          this.membership = this.storageService.get('membership');
          if (this.membership) this.sectionId = this.membership.sectionId;
        } catch (e2) { /* ignore */ }
        this._resolve(this.user);
      }
    } else {
      this._resolve(null);
    }
  }

  /**
   * Verifica se o token está expirado
   * Usa a data de expiração fornecida pelo backend
   */
  private isTokenExpired(token: any): boolean {
    if (!token) {
      return true; // Se não tem token, considera expirado
    }

    // Verifica primeiro se existe a propriedade 'expiration' (formato do DB)
    if (token.expiration) {
      const expirationDate = new Date(token.expiration);
      const currentDate = new Date();
      return currentDate >= expirationDate;
    }

    // Fallback: Se não tem expiration, verifica se tem 'date' e calcula 7 dias
    if (token.date) {
      const TOKEN_VALIDITY_DAYS = 7;
      const createdDate = new Date(token.date);
      const currentDate = new Date();
      const diffInMilliseconds = currentDate.getTime() - createdDate.getTime();
      const diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
      return diffInDays > TOKEN_VALIDITY_DAYS;
    }

    // Se não tem nem expiration nem date, considera expirado
    return true;
  }

  async setupSession(token) {
    // Limpa caches de sessão anterior antes de configurar nova sessão
    this.invalidateAllCaches();

    // Verifica se o token está expirado antes de configurar a sessão
    if (this.isTokenExpired(token)) {
      await this.logout();
      throw new Error('Token expirado');
    }

    this.httpService.setKey(token.token);
    const user = await this.arboService.get('User', token.userId);
    user.token = token;
    this.accounts = user.accounts || [];

    // Tenta restaurar a conta selecionada do localStorage
    const selectedAccountId = localStorage.getItem('selectedAccountId');

    if (selectedAccountId && this.accounts) {
      // Converte para número para comparação correta
      const accountIdNum = parseInt(selectedAccountId, 10);

      // Procura a conta nas contas disponíveis
      this.account = this.accounts.find(acc => acc.id === accountIdNum);

      // Se não encontrou a conta nas disponíveis, use a primeira
      if (!this.account && this.accounts.length > 0) {
        this.account = this.accounts[0];
      }
    } else if (this.accounts && this.accounts.length > 0) {
      // Caso não tenha conta salva, use a primeira disponível
      this.account = this.accounts[0];
    }

    // Sincronize o accountId com o ID da conta atual
    if (this.account) {
      this.accountId = this.account.id;
      // Atualiza o localStorage para garantir consistência
      localStorage.setItem('selectedAccountId', String(this.account.id));
    }

    try {
      user.memberships = await this.clientService.getMemberships();
    } catch (e) {
      user.memberships = [];
    }

    // Fallback: se Client/memberships retornou vazio (pode faltar Client entry no backend),
    // usa Section/list-orgs para construir memberships a partir das organizações do usuário
    if (!user.memberships || user.memberships.length === 0) {
      try {
        const orgs = await this.sectionService.listOrgs();
        if (Array.isArray(orgs) && orgs.length > 0) {
          user.memberships = orgs.map(org => ({
            sectionId: org.id,
            sectionName: org.name,
            name: org.name,
            userId: user.id,
            status: 'active'
          }));
        }
      } catch (e) {
        // Mantém memberships vazio
      }
    }
    
    this.setUser(user);

    try {
      this.membership = this.storageService.get('membership');
    } catch (e) {
      console.warn('Erro ao restaurar membership do localStorage:', e);
      this.membership = null;
    }

    if (this.membership) await this.selectMembership(this.membership);
    else if (user.memberships.length > 0)
      await this.selectMembership(user.memberships[0]);

    this._resolve(this.user);

    // Emite evento para notificar que a conta foi carregada
    this.accountChanged.emit();

    // Inicia prefetch do dashboard em background (fire-and-forget)
    if (this.accountId) this.prefetchDashboardData(this.accountId);
  }

  // Método para trocar de conta
  async selectAccount(accountId) {
    if (!this.accounts) return false;

    const selectedAccount = this.accounts.find(acc => acc.id === accountId);

    if (selectedAccount) {
      this.account = selectedAccount;
      this.accountId = accountId;
      localStorage.setItem('selectedAccountId', String(accountId));

      // Emite evento para notificar que a conta foi alterada
      this.accountChanged.emit();
      return true;
    }

    return false;
  }

  async selectMembership(membership) {
    const previousSectionId = this.sectionId;
    this.membership = membership;
    await this.storageService.store('membership', this.membership);
    if (membership) this.sectionId = membership.sectionId;

    // Emite evento se a organização mudou
    if (previousSectionId != this.sectionId) {
      this.membershipChanged.emit();
    }
  }

  async selectMembershipBySectionId(sectionId) {
    await this.selectMembership(
      this.user.memberships.find((membership) => {
        return membership.sectionId == sectionId;
      })
    );
  }

  setUser(user) {
    this.user = user;
    return this.storageService.store('user', this.user);
  }

  getUser() {
    return this.user;
  }

  saveUser(data) {
    if (this.user) {
      data = Object.assign(this.user, data);
    }
    this.setUser(data);
    return Promise.resolve(this.user);
  }

  async login(login, password) {
    this.httpService.setKey(null);
    this.setUser(null);
    const token = await this.arboService.auth(login, password);
    await this.setupSession(token);
  }

  async logout() {
    const userName = this.getUser()?.profile?.nome || this.getUser()?.login;
    if (userName) {
      this.alertService.toast('Até mais, ' + userName + '!');
    }

    this.httpService.setKey(null);
    this.setUser(null);
    await this.selectMembership(null);

    // Limpa caches de todos os services singleton para evitar dados do usuário anterior
    this.invalidateAllCaches();

    // Reseta estado da sessão
    this.sectionId = 1;
    this.account = null;
    this.accountId = null;
    this.accounts = null;
    localStorage.removeItem('selectedAccountId');

    // Redireciona para a página de login ANTES de recriar a Promise loaded
    // (canActivate faz await this.loaded — se recriar antes, trava)
    await this.router.navigate(['/login']);

    // Recria Promise loaded para a próxima sessão (após navegação concluída)
    this.loaded = new Promise((resolve) => {
      this._resolve = resolve;
    });
  }

  /**
   * Limpa caches em memória de todos os services para evitar
   * vazamento de dados entre sessões de usuários diferentes.
   */
  private invalidateAllCaches(): void {
    // Cada chamada em try/catch individual para nunca quebrar o fluxo
    try { this.sectionService?.invalidateCache?.(); } catch (e) { console.warn('[session] sectionService.invalidateCache:', e); }
    try { this.clientService?.invalidateCache?.(); } catch (e) { console.warn('[session] clientService.invalidateCache:', e); }
    // Usa injector com try/catch robusto (pode falhar se serviço nao estiver pronto)
    try {
      const accountService = this.injector.get(AccountService);
      accountService?.invalidateCache?.();
    } catch (e) { console.warn('[session] accountService.invalidateCache:', e); }
    try {
      const campaignService = this.injector.get(CampaignService);
      campaignService?.invalidateCache?.();
    } catch (e) { console.warn('[session] campaignService.invalidateCache:', e); }
    try {
      const sourceService = this.injector.get(SourceService);
      sourceService?.invalidateCache?.();
    } catch (e) { console.warn('[session] sourceService.invalidateCache:', e); }
    try { this.dashboardService?.clearPrefetch?.(); } catch (e) { console.warn('[session] dashboardService.clearPrefetch:', e); }
  }

  /**
   * Inicia prefetch do Client/graph em paralelo com o resto do setupSession.
   * Usa filtros salvos do localStorage ou defaults (7 dias, todas as redes).
   */
  private prefetchDashboardData(accountId: number): void {
    try {
      // Tenta recuperar filtros salvos
      let networks = ['instagram', 'facebook', 'youtube'];
      let activeFilter = '7days';

      const savedFilters = localStorage.getItem(`dashboard_filters_${this.sectionId}`);
      if (savedFilters) {
        const filters = JSON.parse(savedFilters);
        if (filters.selectedNetworks?.length > 0) networks = filters.selectedNetworks;
        if (filters.activeFilter) activeFilter = filters.activeFilter;
      }

      // Calcula datas baseado no filtro
      const now = new Date();
      const endDate = new Date(now);
      endDate.setHours(23, 59, 59, 999);
      const startDate = new Date(now);

      if (activeFilter === '30days') {
        startDate.setDate(startDate.getDate() - 29);
      } else {
        startDate.setDate(startDate.getDate() - 6); // 7 dias (padrão)
      }
      startDate.setHours(0, 0, 0, 0);

      this.dashboardService.prefetch({
        accountIds: [accountId],
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        networks
      });
    } catch (e) {
      // Falha silenciosa — dashboard fará a chamada normalmente
    }
  }

  async canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean> {
    // Aguarda a restauração da sessão anterior antes de decidir
    await this.loaded;

    if (state.url.substr(0, 6) == '/login') {
      // Permite acesso à página de login
      return true;
    } else {
      const user = this.getUser();
      if (!user) {
        await this.router.navigate(['/login']);
        return false;
      }

      // Verifica se o token do usuário está expirado
      if (user.token && this.isTokenExpired(user.token)) {
        this.alertService.toast('Sua sessão expirou. Por favor, faça login novamente.');
        await this.logout();
        return false;
      }
    }
    return true;
  }
}